import { GetStaticPaths, GetStaticProps } from 'next';
import GridCards from '~/components/business/grid-cards';
import BusinessValues from '~/components/business/business-values';
import ContactUsForm from '~/components/business/contact-us-form';
import Community from '~/components/business/community';
import { Translate, useTranslate } from '~/i18n';
import { NextSeo } from 'next-seo';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import TopSection from '~/components/business/top-section';
import DigitizeBusiness from '~/components/business/digitize-business';
import { getLanguagesPaths } from '~/utils/translate';
import Booking from '~/components/business/booking';
import { useEffect, useRef } from 'react';
import { businessPageViewEvent } from '~/utils/mixpanel-events/business';
import { isJarir } from '~/utils/theme';
import {
   businessTopRegisterBtnEvent,
   businessTopRequestBtnEvent,
} from '~/utils/mixpanel-events/business';
import { publicUrl } from '~/utils/publicUrl';
import Image from 'next/image';
import BusinessType from '~/components/business/business-type';
import ReadyToSell from '~/components/business/ready-to-sell';
import Partners from '~/components/business/partners';

const Business = () => {
   const { locale, translate } = useTranslate();
   const contactUsFormRef = useRef<HTMLDivElement>();

   useEffect(() => {
      businessPageViewEvent();
   }, []);

   const TopSectionItems = {
      title: <Translate id="business:headline" />,
      subtitle: <Translate id="business:with less effort" />,
      description: <Translate id="business:sub headline" />,
      image: '/images/business/business-bg.png',
      buttons: [
         {
            text: <Translate id="business:signup now" />,
            href: `https://pro.halayalla.com//${locale}/supplier/login?utm_source=halayalla.com&utm_medium=referral`,
            className: 'signupnow-top',
            onClick: () => businessTopRegisterBtnEvent(),
         },
         {
            text: <Translate id="business:request a call back" />,
            className: 'learnmore-top !border-[#0552bc] border-2 w-full !bg-white ',
            labelColor: 'text-[#0552bc]',
            onClick: () => {
               contactUsFormRef?.current?.scrollIntoView({
                  block: 'center',
                  inline: 'center',
                  behavior: 'smooth',
               });
               businessTopRequestBtnEvent();
            },
         },
      ],
   };

   const bussinessValues = {
      title: translate({ id: 'business:digitize' }),
      subtitle: translate({ id: 'business:no training' }),
      businessValues: [
         {
            icon: (
               <Image src={publicUrl('images/business/stand-out.png')} width={180} height={160} />
            ),
            name: translate({ id: 'business:more customers' }),
         },
         {
            icon: <Image src={publicUrl('images/business/tech.png')} width={160} height={140} />,
            name: translate({ id: 'business:technology' }),
         },
         {
            icon: (
               <Image src={publicUrl('images/business/increase.png')} width={170} height={160} />
            ),
            name: translate({ id: 'business:improve' }),
         },
      ],
   };

   const CrossHeadsItems: any[] = [
      {
         title: <Translate id={`business:crosshead 1 title`} />,
         description: <Translate id={`business:crosshead 1 description`} />,
         image: '/images/business/illustration-1.png',
      },
      {
         title: <Translate id={`business:crosshead 2 title`} />,
         description: <Translate id={`business:crosshead 2 description`} />,
         image: '/images/business/illustration-2.png',
      },
   ];

   const bussinessValuesContent = {
      title: <Translate id="business:booking categories" />,
      description: <Translate id="business:booking categories description" />,
   };

   const contactUsContent = {
      title: <Translate id="business:join today and start creating experiences" />,
      description: <Translate id="business:get started now" />,
   };

   return (
      <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}>
         <div>
            <NextSeo
               title={
                  !isJarir
                     ? translate({ id: 'common:hala yalla for business' })
                     : translate({ id: 'jarir:sell your experiences on jarir' })
               }
               description={translate({ id: 'seo:business description' })}
               openGraph={{
                  title: translate({ id: 'seo:business title' }),
                  description: translate({ id: 'seo:business description' }),
               }}
            />
            <TopSection contactUsFormRef={contactUsFormRef} content={TopSectionItems} />
            <DigitizeBusiness
               bussinessValues={bussinessValues}
               CrossHeadsItems={CrossHeadsItems}
               showBusinessExperinces={true}
            />
            <BusinessType />
            <BusinessValues content={bussinessValuesContent} />
            {!isJarir && (
               <>
                  <Community />
                  <GridCards />
                  <Partners />
               </>
            )}
            <Booking />
            <ReadyToSell />
            <ContactUsForm
               contactUsFormRef={contactUsFormRef}
               contactUsContent={contactUsContent}
            />
         </div>
      </GoogleReCaptchaProvider>
   );
};

export const getStaticPaths: GetStaticPaths = async () => {
   return {
      paths: getLanguagesPaths(),
      fallback: false,
   };
};

/******************************************************************
 *
 *  THIS PAGE IS USING STATIC SITE GENERATION
 *
 ******************************************************************/

export const getStaticProps: GetStaticProps = async () => {
   return {
      props: {},
   };
};
export default Business;
